import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { PackageCard } from "../components/PackageCard";
import { resolveImageUrl } from "../components/ImageUpload";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { SearchIcon } from "lucide-react";

import { SubcategoryBar } from "../components/SubcategoryBar";
import Testimonials from "../components/Testimonials";

export default function CategoryPage() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [category, setCategory] = useState(null);
  const [packages, setPackages] = useState([]);
  const [maxPrice, setMaxPrice] = useState(Number(params.get("max_price")) || 15000);
  const [q, setQ] = useState(params.get("q") || "");
  const [activeSub, setActiveSub] = useState(params.get("sub") || "");

  useEffect(() => { 
    api.get(`/categories/${slug}`).then((r) => setCategory(r.data)).catch(() => {}); 
  }, [slug]);

  useEffect(() => {
    const t = setTimeout(() => {
      const subParam = activeSub ? `&subcategory=${activeSub}` : "";
      api.get(`/packages?category=${slug}&max_price=${maxPrice}${q ? `&q=${encodeURIComponent(q)}` : ""}${subParam}`)
        .then((r) => setPackages(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [slug, maxPrice, q, activeSub]);

  useEffect(() => {
    if (category) document.title = `${category.name} Decoration Packages — YoursDreamDecoration`;
  }, [category]);

  const handleSubChange = (subSlug) => {
    setActiveSub(subSlug);
    setParams(prev => {
      if (subSlug) prev.set("sub", subSlug);
      else prev.delete("sub");
      return prev;
    });
  };

  return (
    <div data-testid="category-page" className="min-h-screen bg-background">
      <section className="relative h-64 sm:h-80 overflow-hidden">
        <img src={resolveImageUrl(category?.image)} alt="" className="w-full h-full object-cover scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-black/20" />
        <div className="absolute inset-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-12">
          <span className="text-[10px] uppercase tracking-[0.4em] text-white/70 font-bold">Category</span>
          <h1 className="font-serif-display text-4xl sm:text-6xl text-white mt-2 leading-tight drop-shadow-lg">{category?.name || slug}</h1>
          <p className="text-white/80 mt-3 max-w-2xl text-sm sm:text-base font-medium line-clamp-2">{category?.description}</p>
        </div>
      </section>

      {/* Subcategory Bar */}
      <div className="sticky top-[72px] z-20 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <SubcategoryBar 
          subcategories={category?.subcategories || []} 
          activeSub={activeSub} 
          onSelect={handleSubChange} 
        />
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-[280px_1fr] gap-12">
          <aside className="space-y-10 lg:sticky lg:top-32 h-fit" data-testid="filters">
            <div className="bg-card/50 rounded-3xl p-6 border border-border/50 backdrop-blur-sm">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold mb-6 text-muted-foreground flex items-center gap-2">
                <SearchIcon className="w-3 h-3" /> Search
              </h3>
              <Input 
                value={q} 
                onChange={(e) => setQ(e.target.value)} 
                placeholder="Ex: Red roses, 25th birthday..." 
                className="rounded-xl border-border/50 bg-background"
                data-testid="filter-search" 
              />
            </div>

            <div className="bg-card/50 rounded-3xl p-6 border border-border/50 backdrop-blur-sm">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold mb-6 text-muted-foreground">Price Range</h3>
              <div className="text-3xl font-serif-display mb-6">₹{maxPrice.toLocaleString("en-IN")}</div>
              <Slider 
                min={500} 
                max={25000} 
                step={500} 
                value={[maxPrice]} 
                onValueChange={(v) => setMaxPrice(v[0])} 
                data-testid="filter-budget" 
              />
              <div className="flex justify-between mt-4 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                <span>₹500</span>
                <span>₹25k+</span>
              </div>
            </div>

            <Button 
              variant="ghost" 
              onClick={() => { setMaxPrice(15000); setQ(""); setActiveSub(""); setParams({}); }} 
              className="w-full rounded-xl text-xs uppercase tracking-widest font-bold h-12"
              data-testid="filter-reset"
            >
              Reset All Filters
            </Button>
          </aside>

          <div>
            <div className="flex items-center justify-between mb-8">
              <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                Showing <span className="text-foreground">{packages.length}</span> results
              </div>
              <div className="h-px flex-1 mx-6 bg-gradient-to-r from-border/50 to-transparent" />
            </div>

            {packages.length === 0 ? (
              <div className="text-center py-24 bg-card/20 rounded-[2.5rem] border border-dashed border-border" data-testid="no-results">
                <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SearchIcon className="w-8 h-8 text-muted-foreground/50" />
                </div>
                <h3 className="font-serif-display text-2xl mb-2">No matches found</h3>
                <p className="text-muted-foreground text-sm max-w-xs mx-auto">Try adjusting your filters or search keywords to find what you're looking for.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-8">
                {packages.map((p) => <PackageCard key={p.slug} pkg={p} />)}
              </div>
            )}
          </div>
        </div>
      </section>
      <Testimonials />
    </div>
  );
}
